import { useCallback } from 'react';
import { ScrollView } from 'react-native';
import { Flex, Typography, RemoteImage } from '@banhala/ui-kit';
import type {
  MultiCheckboxRow as MultiCheckboxRowType,
  MultiCheckboxItem,
  MultiCheckboxMoreCta,
} from './types';
import type { IScreensLogging } from '../../../../api/types/IScreenLogging';

type MultiCheckboxRowProps = {
  row: MultiCheckboxRowType;
  appliedValues: Set<string>;
  onToggleItem: (queryName: string, queryValue: string, itemText: string, itemLogging?: IScreensLogging) => void;
  onPressMoreCta: (moreCta: MultiCheckboxMoreCta) => void;
};

export const MultiCheckboxRow = ({
  row,
  appliedValues,
  onToggleItem,
  onPressMoreCta,
}: MultiCheckboxRowProps) => {
  const handlePressItem = useCallback(
    (item: MultiCheckboxItem) => {
      onToggleItem(row.query_name, item.query_value, item.text, item.logging);
    },
    [onToggleItem, row.query_name],
  );

  const handlePressMore = useCallback(() => {
    if (row.more_cta) {
      onPressMoreCta(row.more_cta);
    }
  }, [row.more_cta, onPressMoreCta]);

  return (
    <Flex.Row alignItems="center" height={32}>
      <Flex width={40} margin={{ left: 16, right: 4 }}>
        <Typography
          type="subtitle3"
          color="content_primary"
          numberOfLines={1}
        >
          {row.title}
        </Typography>
      </Flex>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        alwaysBounceHorizontal
        contentContainerStyle={scrollContentStyle}
      >
        {row.possible_items.map(item => {
          const isSelected = appliedValues.has(item.query_value);
          return (
            <CheckboxItem
              key={item.query_value}
              item={item}
              isSelected={isSelected}
              onPress={handlePressItem}
            />
          );
        })}
        {row.more_cta != null && (
          <Flex
            onPress={handlePressMore}
            justifyContent="center"
            padding={{ horizontal: 4 }}
          >
            <Typography
              type="body3"
              color="content_secondary"
              textDecorationLine="underline"
            >
              {row.more_cta.title}
            </Typography>
          </Flex>
        )}
      </ScrollView>
    </Flex.Row>
  );
};

const CheckboxItem = ({
  item,
  isSelected,
  onPress,
}: {
  item: MultiCheckboxItem;
  isSelected: boolean;
  onPress: (item: MultiCheckboxItem) => void;
}) => {
  const handlePress = useCallback(() => {
    onPress(item);
  }, [onPress, item]);

  return (
    <Flex.Row
      alignItems="center"
      onPress={handlePress}
      padding={{ horizontal: 4 }}
    >
      {item.image != null &&
        item.image.width != null &&
        item.image.height != null &&
        item.image.radius != null && (
        <Flex
          margin={{ right: 4 }}
          overflow="hidden"
          width={item.image.width}
          height={item.image.height}
          borderRadius={item.image.radius}
          {...(item.image.border_width != null && {
            borderWidth: item.image.border_width,
            borderColorValue: '#0000001f',
          })}
        >
          <RemoteImage
            uri={item.image.image_url}
            width={item.image.width}
            height={item.image.height}
            resizeMode="cover"
          />
        </Flex>
      )}
      <Typography
        type={isSelected ? 'subtitle3' : 'body3'}
        color={isSelected ? 'content_primary' : 'content_secondary'}
      >
        {item.text}
      </Typography>
    </Flex.Row>
  );
};

const scrollContentStyle = {
  flexDirection: 'row' as const,
  alignItems: 'center' as const,
  gap: 8,
  paddingRight: 16,
};
